/**
 * Biography — write it once, and it reaches every page that introduces the artist.
 *
 * The engine has been writing chapters into Postgres for months with no screen to see them on,
 * and publishing them changed nothing a visitor could reach. This is both halves: the chapters,
 * editable, and an honest statement of where publishing sends them.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Globe, Loader2, Sparkles, Store, Link2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RichTextEditor } from '@/components/common/editor/RichTextEditor'

import { useBio, usePublishBio, useRegenerateBio } from '../hooks/useBio'
import type { BioSection } from '../api/bioApi'

export function BiographyPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useBio()
  const regenerate = useRegenerateBio()
  const publish = usePublishBio()

  const [sections, setSections] = useState<BioSection[]>([])
  const [toMarketplace, setToMarketplace] = useState(true)
  const [toStorefront, setToStorefront] = useState(true)
  const [confirming, setConfirming] = useState(false)

  // Edit the draft when there is one, otherwise what is already live. A draft exists precisely
  // because it has not been read yet, so it is the thing that needs attention.
  useEffect(() => {
    if (!data) return
    setSections(data.hasDraft ? data.draft : data.published)
  }, [data])

  const editing = data?.hasDraft ? 'draft' : 'published'
  const update = (i: number, patch: Partial<BioSection>) =>
    setSections((prev) => prev.map((s, n) => (n === i ? { ...s, ...patch } : s)))

  if (isLoading) {
    return (
      <div className="space-y-6 p-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const empty = sections.length === 0

  return (
    <div className="space-y-6 p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">Biography</h1>
              {!empty && (
                <Badge variant={editing === 'draft' ? 'secondary' : 'default'}>
                  {editing === 'draft' ? 'Unpublished draft' : 'Live'}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              The story of your practice, told in chapters. One place to write it; three places it
              appears.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>
            {regenerate.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            {empty ? 'Write a first draft' : 'Rewrite the draft'}
          </Button>
          <Button
            disabled={empty || publish.isPending}
            onClick={() => setConfirming(true)}
          >
            {publish.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            This is accurate — publish it
          </Button>
        </div>
      </div>

      {editing === 'draft' && !empty && (
        <Alert>
          <AlertDescription>
            This draft is not public, and nothing will publish it but you. It was written from your
            own work and words — read it through, change anything that is wrong, and only then say
            it is accurate.
          </AlertDescription>
        </Alert>
      )}

      {/* Publishing is the artist agreeing to an account of their own life. It gets a confirm
          step naming where the words will appear, because the consequential part is not the save
          — it is that these become the first thing a stranger reads about them. */}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish this as your biography?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  It was drafted from your work, so read it as your own words before it becomes
                  them. You can change it whenever you like.
                </p>
                <p>These {sections.length} chapters will appear on:</p>
                <ul className="list-disc pl-5">
                  {toMarketplace && <li>your marketplace page, replacing the bio there now</li>}
                  {toStorefront && <li>the About section of your site</li>}
                  <li>your link-in-bio profile</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                publish.mutate({
                  sections,
                  targets: { publishToMarketplace: toMarketplace, publishToStorefront: toStorefront },
                })
              }
            >
              Yes, publish it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {empty ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Sparkles className="h-10 w-10 mx-auto text-muted-foreground/50" />
                <h3 className="mt-4 font-medium">No biography yet</h3>
                <p className="text-muted-foreground mt-1 max-w-md mx-auto">
                  A first draft is written from your uploaded work and anything you have already
                  written about it. Nothing is published until you say so.
                </p>
                <Button className="mt-6" onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>
                  {regenerate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Write a first draft
                </Button>
              </CardContent>
            </Card>
          ) : (
            sections.map((section, i) => (
              <Card key={section.id ?? i}>
                <CardHeader className="pb-3">
                  <Label htmlFor={`bio-title-${i}`} className="text-xs uppercase tracking-wide text-muted-foreground">
                    Chapter {i + 1}
                  </Label>
                  <Input
                    id={`bio-title-${i}`}
                    value={section.title ?? ''}
                    onChange={(e) => update(i, { title: e.target.value })}
                    className="text-lg font-medium"
                  />
                </CardHeader>
                <CardContent>
                  <RichTextEditor
                    value={section.contentHtml ?? ''}
                    onChange={(html) => update(i, { contentHtml: html })}
                    placeholder="Write this chapter…"
                  />
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Where it publishes</CardTitle>
              <CardDescription>Every page that introduces you, from one text.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Store className="h-4 w-4" /> Marketplace page
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your collection page — where most people find your prints.
                  </p>
                </div>
                <Switch checked={toMarketplace} onCheckedChange={setToMarketplace} />
              </div>

              <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Globe className="h-4 w-4" /> Your site
                  </p>
                  <p className="text-xs text-muted-foreground">The About section of your storefront.</p>
                </div>
                <Switch checked={toStorefront} onCheckedChange={setToStorefront} />
              </div>

              <div className="flex items-start justify-between gap-3 rounded-lg border p-3 opacity-70">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Link2 className="h-4 w-4" /> Your profile
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your link-in-bio page reads your published chapters directly — always in step.
                  </p>
                </div>
                <Badge variant="outline">Always</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
